import React, { useContext } from 'react';
import { useEffect } from 'react';
import { IntersectionObserverView } from '@kibt/meta-ui-krn';
import { logger } from '@/common/log';
import { afterPv } from '@/common/utils/afterPv';
import { FlatListContext } from '../RtlLayout/FlatList';
import { View } from 'react-native';

type ViewVisibleProp = Partial<React.ComponentProps<typeof IntersectionObserverView> & { onVisible: () => void }>;

/**
 * ViewVisible
 * 如果该组件被ScrollView包裹，需要对该ScrollView设置flex:1
 * @warning 1. ViewVisible需要包裹曝光元素，如果使用自封闭<ViewVisible />曝光不会生效(已兜底，现在会生效，但可能出现样式问题)；
 *          2. 当包裹的首层元素是fixed定位时，曝光不会生效，需要将ViewVisible放到该fixed元素内部；
 *          3. ViewVisible包裹的元素需要尽可能的细粒度；
 *          4. 如果不需要曝光检测，可以使用LogShow组件进行埋点的上报；
 *
 * @warning FlatList不要用ViewVisible， 改成LogShow组件
 */

export function ViewVisible(props: React.PropsWithChildren<ViewVisibleProp>) {
    const { children, onVisible } = props;
    const _props = { ...props };

    if (onVisible) {
        _props.onInView = onVisible;
        delete _props.onVisible;
    }

    return children ? (
        <IntersectionObserverView throttle={1000} {..._props}>
            {children}
        </IntersectionObserverView>
    ) : (
        <IntersectionObserverView throttle={1000} {..._props}>
            {/* 兜底自封闭标签的曝光 */}
            <View style={{ height: 1, width: 1 }} />
        </IntersectionObserverView>
    );
}

/**
 * @description
 * 当不需要曝光检测时，使用LogShow进行埋点上报
 * eg1:
 * <LogShow onVisible={() => {
 *      // 调用埋点曝光
 * }}>
 *          <Text>要曝光的元素</Text>
 * </LogShow>
 *
 * eg2：
 * <LogShow action="曝光埋点的action" params={{ 埋点参数 }}>
 *      <Text>要曝光的元素</Text>
 * </LogShow>
 *
 * eg3：FlatList场景
 * import { FlatList } from '@/components/RtlLayout/FlatList'; // 增强后的FlatList
 * <FlatList
 *      renderItem={(item) => {
 *          return <LogShow
 *                      itemKey={item.key}
 *                      action="曝光埋点的action" params={{ 埋点参数 }}>
 *                         <Text>要曝光的元素</Text>
 *                 </LogShow>
 *      }}
 * />
 */
export function LogShow(
    props: React.PropsWithChildren<{
        // 自动曝光埋点信息
        action?: string;
        params?: Record<string, any>;
        // 回调事件，可以自行曝光处理
        onVisible?: () => void;
        afterPv?: boolean;
        // FlatList item key
        itemKey?: string;
    }>,
) {
    // 获取FlatListContext 上面的曝光元素信息
    const flatListState = useContext(FlatListContext);

    useEffect(() => {
        const callback = () => {
            if (props?.afterPv) {
                afterPv.then(() => {
                    if (props.onVisible) {
                        props?.onVisible?.();
                    } else if (props.action) {
                        logger.sendShow({
                            action: props?.action,
                            params: props?.params,
                        });
                    }
                });
            } else {
                if (props.onVisible) {
                    props?.onVisible?.();
                } else if (props.action) {
                    logger.sendShow({
                        action: props?.action,
                        params: props?.params,
                    });
                }
            }
        };

        // 如果有Context.Provider，说明是FlatList场景使用的，走flatlist的callback逻辑
        if (flatListState.initContext) {
            if (!props.itemKey) {
                throw new Error('[LogShow] FlatList场景下的LogShow组件使用，必须传itemKey，请阅读组件使用规范');
            }

            // 曝光回调里面处理
            flatListState.listeners.push({
                itemKey: props.itemKey,
                listener: callback,
            });
        } else {
            // 如果不是FlatList场景，直接调用callback
            callback();
        }
    }, []);

    return <>{props.children}</>;
}
